import { useMemo, useState } from 'react';
import { Card, CardBody, Input, Button, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Spinner } from '@components/ui';
import { useDebounce } from '@/hooks/useDebounce';
import { useRequestLogs } from '../hooks/useDevTools';
import type { LogSearchFilters, RequestLog } from '../types/devtools.types';

const PAGE_SIZE = 10;
const DEBOUNCE_MS = 900;

const EMPTY_FILTERS: LogSearchFilters = {
  serviceName: '',
  serviceUrl: '',
  clientDevice: '',
  clientBrowserName: '',
  userId: '',
  searchText: '',
};

function LogActionButton({ label, url, testId }: { label: string; url: string | null; testId?: string }) {
  const enabled = !!url && url.toLowerCase().startsWith('http');

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!enabled}
      title={!enabled ? url || '' : undefined}
      onClick={() => enabled && window.open(url!, '_blank')}
      data-testid={testId}
    >
      {label}
    </Button>
  );
}

export function RequestLogsTab() {
  const [filters, setFilters] = useState<LogSearchFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const debouncedFilters = useDebounce(filters, DEBOUNCE_MS);

  const { data: logs = [], isLoading } = useRequestLogs(debouncedFilters);

  const paginatedLogs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return logs.slice(start, start + PAGE_SIZE);
  }, [logs, page]);

  const totalPages = Math.max(1, Math.ceil(logs.length / PAGE_SIZE));

  const handleFilterChange = (field: keyof LogSearchFilters) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleClearFilters = () => {
    setPage(1);
    setFilters(EMPTY_FILTERS);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300">Filtros</h3>
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-sm text-primary-600 hover:underline"
              data-testid="devtools-request-logs-tab-clear-filters-button"
            >
              Limpiar filtros
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input placeholder="Nombre del servicio" value={filters.serviceName} onChange={handleFilterChange('serviceName')} data-testid="devtools-request-logs-tab-service-name-input" />
            <Input placeholder="URL del servicio" value={filters.serviceUrl} onChange={handleFilterChange('serviceUrl')} data-testid="devtools-request-logs-tab-service-url-input" />
            <Input placeholder="Cliente" value={filters.clientDevice} onChange={handleFilterChange('clientDevice')} data-testid="devtools-request-logs-tab-client-device-input" />
            <Input placeholder="Navegador" value={filters.clientBrowserName} onChange={handleFilterChange('clientBrowserName')} data-testid="devtools-request-logs-tab-client-browser-input" />
            <Input placeholder="ID de usuario" value={filters.userId} onChange={handleFilterChange('userId')} data-testid="devtools-request-logs-tab-user-id-input" />
            <Input placeholder="Búsqueda por palabra" value={filters.searchText} onChange={handleFilterChange('searchText')} data-testid="devtools-request-logs-tab-search-text-input" />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {isLoading ? (
            <div className="flex justify-center py-12" data-testid="devtools-request-logs-tab-loading-spinner">
              <Spinner size="lg" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-secondary-500 dark:text-secondary-400 py-12 text-center" data-testid="devtools-request-logs-tab-empty-message">
              No se han encontrado registros.
            </p>
          ) : (
            <>
              <Table data-testid="devtools-request-logs-tab-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Nombre del servicio</TableHead>
                    <TableHead>Tiempo de ejecución</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Navegador</TableHead>
                    <TableHead>Componentes de la solicitud</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedLogs.map((log: RequestLog) => (
                    <TableRow key={log.id} data-testid={`devtools-request-logs-tab-row-${log.id}`}>
                      <TableCell>
                        {log.createdDate ? new Date(log.createdDate).toLocaleString('es-PA') : '-'}
                      </TableCell>
                      <TableCell>
                        <p className="font-semibold">{log.serviceName}</p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">{log.serviceUrl}</p>
                      </TableCell>
                      <TableCell>{log.executionTime?.toFixed(4)} s</TableCell>
                      <TableCell>
                        <p>{log.clientDevice}</p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">{log.clientIp}</p>
                      </TableCell>
                      <TableCell>{log.clientBrowserName}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <LogActionButton label="Curl" url={log.curlUrl} testId={`devtools-request-logs-tab-curl-button-${log.id}`} />
                          <LogActionButton label="Payload" url={log.payloadUrl} testId={`devtools-request-logs-tab-payload-button-${log.id}`} />
                          <LogActionButton label="Response" url={log.responseUrl} testId={`devtools-request-logs-tab-response-button-${log.id}`} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="devtools-request-logs-tab-prev-page-button">
                    Anterior
                  </Button>
                  <span className="text-sm text-secondary-500 dark:text-secondary-400">
                    Página {page} de {totalPages}
                  </span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="devtools-request-logs-tab-next-page-button">
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
